import {
  MutationFunction,
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { ReactNode, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';

import { setAlert } from 'routes/AppContainer/actions';
import { selectCurrentIdentityLanguage } from 'routes/AppContainer/selectors';
import Helium from 'utils/helium';
import { queryRequest } from 'utils/queryRequest';
import { createErrorAlertArgs } from 'utils/alertHelpers';

import { invokeOrReturn } from '../invokeOrReturn';

type MutationArgs<Endpoint extends keyof Api.ClientSpec> = {
  payload?: ApiClientPayload<Endpoint>;
  query?: ApiClientQuery<Endpoint>;
  [key: string]: any;
} & (Api.ClientSpec[Endpoint]['pathParams'] extends []
  ? { apiEndpointRequirements?: never }
  : { apiEndpointRequirements: Api.ClientSpec[Endpoint]['pathParams'] });

type ApiClientPayload<Endpoint extends keyof Api.ClientSpec> =
  Endpoint extends keyof Api.Payload ? Api.Payload[Endpoint] : never;

type ApiClientQuery<Endpoint extends keyof Api.ClientSpec> =
  Api.ClientSpec[Endpoint]['queryOffset'] extends -1
    ? never
    : Parameters<Api.Client[Endpoint]>[Api.ClientSpec[Endpoint]['queryOffset']];

type BaseOpMutationOptions<TData, TVariables> = UseMutationOptions<
  TData,
  OP.ApiError,
  TVariables
> & {
  onSuccessMessage?:
    | string
    | ((mutationResponse: TData, mutationArgs: TVariables) => string)
    | null;
  onSuccessCallback?: (
    mutationResponse: TData,
    mutationArgs: TVariables,
  ) => void | Promise<void>;
  onErrorMessage?: string | ((error: OP.ApiError) => ReactNode) | null;
  shouldSuppressErrorMessage?:
    | boolean
    | ((error: OP.ApiError) => boolean | void);
  onErrorCallback?: (
    error: OP.ApiError,
    variables?: TVariables,
  ) => void | Promise<void>;
  queryKeysToInvalidate?: (string | number)[][];
  headers?: Record<string, string>;
  overrideApiClient?: typeof Helium.client;
  /**
   * Setting this to `false` will allow errors to bubble up.
   * Useful when using the mutation inside other mutations.
   * @default true
   */
  catchMutateAsyncError?: boolean;
  bypassTriggers?: boolean;
} & (
    | {
        catchMutateAsyncError: false;
        onErrorCallback?: undefined;
      }
    | {
        catchMutateAsyncError?: true;
        onErrorCallback?: (error: OP.ApiError, variables?: TVariables) => void;
      }
  );

type UseOpMutationOptions<
  Endpoint extends keyof Api.ClientSpec,
  TData,
  TVariables,
> = BaseOpMutationOptions<TData, TVariables> &
  (
    | {
        apiEndpointName: Endpoint;
        mutationFn?: undefined;
      }
    | {
        apiEndpointName?: undefined;
        mutationFn: MutationFunction<TData, TVariables>;
      }
  );

export const useOpMutation = <
  Endpoint extends keyof Api.ClientSpec,
  TData = Utils.GetApiReturnType<Endpoint>,
  TVariables = MutationArgs<Endpoint>,
>({
  apiEndpointName,
  onSuccessMessage,
  onErrorMessage,
  onSuccessCallback,
  onErrorCallback, // Will only affect mutateAsync (need to prevent mutate using types in separate PR)
  queryKeysToInvalidate,
  shouldSuppressErrorMessage = false,
  headers,
  overrideApiClient,
  catchMutateAsyncError = true,
  bypassTriggers,
  ...useOpMutationOptions
}: UseOpMutationOptions<Endpoint, TData, TVariables>) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // TODO - get the language based on authed page logic if not in identity
  /** we first look at the identity language and set the language to that.
   * If that is not set, we then fall back to the local storage setting.
   * If that is not set, then we fall back to the browser locale.
   * And if that is not supported then we use English (en). */
  const currentIdentityLanguage = useSelector(selectCurrentIdentityLanguage());

  const defaultMutationFn = (async ({
    apiEndpointRequirements = [],
    payload: rawPayload,
    query: rawQuery,
  }: MutationArgs<Endpoint>) => {
    const parametersArray: Parameters<Api.Client[Endpoint]>[number][] = [
      ...apiEndpointRequirements,
    ];

    if (rawPayload) {
      parametersArray.push(rawPayload);
    }
    if (rawQuery) {
      parametersArray.push(rawQuery);
    }

    const result = await queryRequest({
      apiEndpointName: apiEndpointName!,
      parameters: parametersArray as Parameters<Api.Client[Endpoint]>,
      currentIdentityLanguage,
      headers,
      overrideApiClient,
      bypassTriggers,
    });

    if (result.statusCode >= 400) {
      throw result.json;
    }

    return result.json?.data;

    // Cast is necessary here as `Arguments` may be different from
    // `MutationArgs<T>` when the hook is called. Specially when no
    // `apiEndpointName` is set.
  }) as MutationFunction<TData, TVariables>;

  const defaultOnError = (error: OP.ApiError) => {
    const suppressMessage = invokeOrReturn(shouldSuppressErrorMessage, [error]);
    if (!suppressMessage) {
      const errorMessage =
        invokeOrReturn(onErrorMessage, [error]) ||
        String(error.localizedMessage || error.message || '');
      dispatch(
        setAlert(
          ...createErrorAlertArgs({ alertMessage: errorMessage, error }),
        ),
      );
    }
  };

  const defaultOnSuccess = async (
    mutationResponse: TData,
    mutationArgs: TVariables,
  ) => {
    const successMessage = invokeOrReturn(onSuccessMessage, [
      mutationResponse,
      mutationArgs,
    ]);

    if (successMessage) {
      dispatch(setAlert('success', successMessage));
    }

    if (onSuccessCallback) {
      await onSuccessCallback(mutationResponse, mutationArgs);
    }

    if (queryKeysToInvalidate) {
      queryKeysToInvalidate.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    }
  };

  const {
    mutateAsync: rawMutateAsync,
    // mutate: _mutate, // TODO - uncomment in PR to prevent mutate from being used
    ...rest
  } = useMutation<TData, OP.ApiError, TVariables>({
    mutationKey: apiEndpointName ? [apiEndpointName] : undefined,
    mutationFn: defaultMutationFn,
    onError: defaultOnError,
    onSuccess: defaultOnSuccess,
    // Following options might override the above
    ...useOpMutationOptions,
  });

  const mutateAsync = useCallback<typeof rawMutateAsync>(
    async (variables, options) => {
      try {
        return await rawMutateAsync(variables, options);
      } catch (error) {
        const OpError = error as OP.ApiError;

        if (OpError.statusCode >= 500) {
          Sentry.captureException(OpError);
        }

        if (onErrorCallback) {
          await onErrorCallback(OpError, variables);
        }

        return undefined as TData;
      }
    },
    [onErrorCallback, rawMutateAsync],
  );

  return {
    /**
     * When using useOpMutation directly, we want to catch the error in the custom
     * mutateAsync function so that we don't need to wrap each call to mutateAsync
     * in a try/catch block.
     *
     * However, when using useOpMutation as a hook in another useOpMutation hook
     * within the mutationFn, we want to allow the error to bubble up so that the
     * parent hook can handle the error and block in subsequent code from running.
     */
    mutateAsync: catchMutateAsyncError ? mutateAsync : rawMutateAsync,
    ...rest,
    /**
     * @deprecated Use mutateAsync whenever possible.
     * {@link onErrorCallback} will not work when calling this function
     */
    mutate: rest.mutate,
  };
};
